import {
	Box,
	Button,
	Card,
	CardActions,
	CardContent,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FormType } from '@/api/enum/formType.enum';
import { useEffect, useRef, useState } from 'react';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import TaskOutlinedIcon from '@mui/icons-material/TaskOutlined';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import useModal from '@/common/components/ConfirmationDialog/useModal';
import {
	useEditMemberApplicationMutation,
	useGetMemberApplicationDetailQuery,
} from '@/api/forms-management/memberApplication';
import EditMemberInfoForm from './components/EditMemberInfoForm';
import { FormManagementStatus } from '@/api/enum/common.enum';
import { useGetAllEnumListQuery } from '@/api/common';
import EditContactInfoForm from './components/EditContactInfoForm';
import EditEmergencyContactForm from './components/EditEmergencyContactForm';
import EditEducationWorkLiveForm from './components/EditEducationWorkLiveForm';
import EditMentalHealthInformationForm from './components/EditMentalHealthInformationForm';
import EditConsentForm from './components/EditConsentForm';
import EditAcceptApplicationForm from './components/EditAcceptApplicationForm';

export default function EditMemberApplicationPage() {
	const { t } = useTranslation();
	const [type, setType] = useState<FormType>(FormType.create);
	const navigate = useNavigate();
	const location = useLocation();
	const memberListType = location.pathname.includes('admission-request-list')
		? 'admission-request-list'
		: 'member-list';
	const { preAdmissionId, memberApplicationId, admissionRequestId } =
		useParams();
	const showModal = useModal();
	const [editMemberApplication] = useEditMemberApplicationMutation();

	const { data: allEnumList } = useGetAllEnumListQuery(null, {
		refetchOnMountOrArgChange: true,
	});

	const { data: details }: any = useGetMemberApplicationDetailQuery(
		memberApplicationId || '',
		{
			refetchOnMountOrArgChange: true,
		}
	);
	const memberInfoRef = useRef<{
		getFormData: () => Promise<any>;
		validateForm: () => Promise<boolean>;
	}>(null);
	const contactInfoRef = useRef<{
		getFormData: () => Promise<any>;
		validateForm: () => Promise<boolean>;
	}>(null);
	const emergencyContactRef = useRef<{
		getFormData: () => Promise<any>;
		validateForm: () => Promise<boolean>;
	}>(null);
	const educationWorkLiveRef = useRef<{
		getFormData: () => Promise<any>;
		validateForm: () => Promise<boolean>;
	}>(null);
	const mentalHealthInformationRef = useRef<{
		getFormData: () => Promise<any>;
		validateForm: () => Promise<boolean>;
	}>(null);
	const consentRef = useRef<{
		getFormData: () => Promise<any>;
		validateForm: () => Promise<boolean>;
	}>(null);
	const acceptApplicationRef = useRef<{
		getFormData: () => Promise<any>;
		validateForm: () => Promise<boolean>;
	}>(null);

	const handleSubmit = async (type: string) => {
		const results = await Promise.all([
			memberInfoRef.current?.validateForm() || Promise.resolve(false),
			contactInfoRef.current?.validateForm() || Promise.resolve(false),
			emergencyContactRef.current?.validateForm() || Promise.resolve(false),
			educationWorkLiveRef.current?.validateForm() || Promise.resolve(false),
			mentalHealthInformationRef.current?.validateForm() ||
				Promise.resolve(false),
			consentRef.current?.validateForm() || Promise.resolve(false),
			acceptApplicationRef.current?.validateForm() || Promise.resolve(false),
		]);

		console.log(results);

		const validate = results.some((result) => result === false);
		if (type == 'verify') {
			enqueueSnackbar(
				validate
					? t('snackbar.fieldsVerifiedFalse')
					: t('snackbar.fieldsVerifiedTrue'),
				{
					variant: validate ? 'error' : 'success',
					anchorOrigin: { horizontal: 'center', vertical: 'top' },
					autoHideDuration: 3000,
					action: (key) => {
						return <CloseIcon onClick={() => closeSnackbar(key)} />;
					},
				}
			);
			return;
		}
		if (validate) return;

		const {
			branchId,
			userId,
			nameEn,
			nameZh,
			ageSource,
			dateOfBirth,
			estimatedAge,
			sex,
			hkid,
			maritalStatus,
		} = {
			...(await memberInfoRef.current?.getFormData()),
		};
		const {
			phone,
			homeTel,
			email,
			addressLanguage,
			region,
			district,
			subdistrict,
			estateCourtVillage,
			roadStreet,
			roadStreetNo,
			buildingBlock,
			buildingBlockSpecify,
			floor,
			flat,
		} = {
			...(await contactInfoRef.current?.getFormData()),
		};
		const {
			hasEmergencyContact,
			emergencyContactName,
			emergencyContactAge,
			emergencyContactRelationship,
			emergencyContactAddress,
			emergencyContactTelNo,
		} = {
			...(await emergencyContactRef.current?.getFormData()),
		};
		const { education, workStatus, livingStatus, financialConditionList } = {
			...(await educationWorkLiveRef.current?.getFormData()),
		};
		const {
			followUpClinicPsychiatry,
			psychiatricDisordersTypeList,
			psychiatricDisordersTypeSpecify,
			rehabilitationServicesWaitingList,
			rehabilitationServicesWaitingSpecify,
		} = {
			...(await mentalHealthInformationRef.current?.getFormData()),
		};
		const {
			mailRequest,
			applicantSignatureDate,
			guardianName,
			guardianSignatureDate,
		} = {
			...(await consentRef.current?.getFormData()),
		};
		const {
			acceptApplication,
			approachSource,
			referredOrganisation,
			mentalHealthConditionList,
			serviceRecommendedList,
			serviceRecommendedSpecify,
			membershipApplicationRemarks,
			interventionWorker,
			meetingDate,
			onDutyStaffSignatureDate,
		} = {
			...(await acceptApplicationRef.current?.getFormData()),
		};

		const formData = {
			branchId,
			userId,
			nameEn,
			nameZh,
			ageSource,
			dateOfBirth,
			estimatedAge,
			sex,
			hkid,
			maritalStatus,
			acceptApplication,
			approachSource,
			referredOrganisation,
			mentalHealthConditionList,
			serviceRecommendedList,
			serviceRecommendedSpecify,
			membershipApplicationRemarks,
			interventionWorker,
			meetingDate,
			onDutyStaffSignatureDate,
			mailRequest,
			applicantSignatureDate,
			guardianName: guardianName || null,
			guardianSignatureDate: guardianSignatureDate || null,
			followUpClinicPsychiatry,
			psychiatricDisordersTypeList,
			psychiatricDisordersTypeSpecify,
			rehabilitationServicesWaitingList,
			rehabilitationServicesWaitingSpecify,
			education,
			workStatus,
			livingStatus,
			financialConditionList,
			hasEmergencyContact,
			emergencyContactName,
			emergencyContactAge,
			emergencyContactRelationship,
			emergencyContactAddress,
			emergencyContactTelNo,
			phone,
			homeTel,
			email,
			addressLanguage,
			region,
			district,
			subdistrict,
			estateCourtVillage,
			roadStreet,
			roadStreetNo,
			buildingBlock,
			buildingBlockSpecify,
			floor,
			flat,

			id: details?.data.id || '',
			formStatus:
				type == 'draft'
					? FormManagementStatus.draft
					: FormManagementStatus.submitted,
		};

		if (type == 'submit') {
			showModal((props: any) => (
				<ConfirmationDialog
					{...props}
					showCloseIcon={false}
					title={t('preAdmission.submitButtonTitle')}
					titleProps={{ variant: 'h6' }}
					description={
						<Typography variant='body1'>
							{t('preAdmission.submitButtonText')}
						</Typography>
					}
					cancellationText={t('button.cancel')}
					confirmationText={t('button.confirm')}
					confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
					otherButtonText={t('button.saveDraft')}
					otherButtonProps={{ type: 'submit', variant: 'contained' }}
					onConfirm={async () => {
						saveApi({
							...formData,
							formStatus: FormManagementStatus.submitted,
						});
					}}
					onOtherConfirm={async () => {
						saveApi({ ...formData, formStatus: FormManagementStatus.draft });
					}}
				/>
			));
		} else if (type == 'draft') {
			saveApi({ ...formData, formStatus: FormManagementStatus.draft });
		}
	};

	const saveApi = async (formData: any) => {
		const response = await editMemberApplication(formData);
		if ('data' in response) {
			enqueueSnackbar(
				formData.formStatus == FormManagementStatus.submitted
					? t('snackbar.fieldSynchronizationSuccess')
					: t('snackbar.draftEnquiryRecordSuccess'),
				{
					variant: 'success',
					anchorOrigin: { horizontal: 'center', vertical: 'top' },
					autoHideDuration: 3000,
					action: (key) => {
						return <CloseIcon onClick={() => closeSnackbar(key)} />;
					},
				}
			);
			navigate(
				preAdmissionId
					? `/pre-admission/pre-admission-list/detail/${preAdmissionId}/forms-management`
					: `/member-management/${memberListType}/detail/${admissionRequestId}/forms-management`
			);
		}
	};

	return (
		<>
			<Box sx={{ width: '100%' }}>
				<Card sx={{ marginTop: 3 }}>
					<CardContent sx={{ padding: '24px' }}>
						<Typography variant='h4' marginBottom={'38px'}>
							{t('drawerListItems.newMemberApplication')}
						</Typography>
						<EditMemberInfoForm
							ref={memberInfoRef}
							type={type}
							setType={setType}
							details={details?.data}
						/>
						<EditContactInfoForm
							ref={contactInfoRef}
							type={type}
							setType={setType}
							details={details?.data}
						/>
						<EditEmergencyContactForm
							ref={emergencyContactRef}
							type={type}
							setType={setType}
							details={details?.data}
						/>
						<EditEducationWorkLiveForm
							ref={educationWorkLiveRef}
							type={type}
							setType={setType}
							details={details?.data}
						/>
						<EditMentalHealthInformationForm
							ref={mentalHealthInformationRef}
							type={type}
							setType={setType}
							details={details?.data}
							allEnumList={allEnumList}
						/>
						<EditConsentForm
							ref={consentRef}
							type={type}
							setType={setType}
							details={details?.data}
						/>
						<EditAcceptApplicationForm
							ref={acceptApplicationRef}
							type={type}
							setType={setType}
							details={details?.data}
							allEnumList={allEnumList}
						/>
					</CardContent>

					<CardActions sx={{ justifyContent: 'end', padding: '24px' }}>
						<Button
							type='submit'
							onClick={() =>
								navigate(
									preAdmissionId
										? `/pre-admission/pre-admission-list/detail/${preAdmissionId}/forms-management`
										: `/member-management/${memberListType}/detail/${admissionRequestId}/forms-management`
								)
							}
						>
							{t('button.cancel')}
						</Button>
						<Button
							type='submit'
							variant='contained'
							onClick={() => handleSubmit('draft')}
							endIcon={<EditNoteOutlinedIcon />}
						>
							{t('button.saveDraft')}
						</Button>
						<Button
							type='submit'
							variant='contained'
							onClick={() => handleSubmit('verify')}
							endIcon={<TaskOutlinedIcon />}
						>
							{t('button.verifyFields')}
						</Button>
						<Button
							type='submit'
							variant='contained'
							onClick={() => handleSubmit('submit')}
							endIcon={<CheckCircleOutlineIcon />}
						>
							{t('button.complete')}
						</Button>
					</CardActions>
				</Card>
			</Box>
		</>
	);
}
